import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import { auth, storage } from '../firebase'
import { compressImage, type CompressResult } from './compress'

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024
/** PDFs skip compression, so they get a larger ceiling. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024
export const ACCEPTED_FILE_TYPES = ['application/pdf']

export class UploadError extends Error {}

function extensionFor(file: File): string {
  const fromName = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : ''
  if (fromName && fromName.length <= 5) return fromName
  return (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg')
}

export interface UploadResult extends Omit<CompressResult, 'file'> { url: string }

/**
 * Compresses and uploads one image, resolving to its URL and what the
 * compression saved. Compression lives here so every caller gets it.
 *
 * Images deliberately do NOT go into the Firestore document: a note is capped
 * at 1 MB, and base64 in the doc would blow that on a single photo.
 */
export async function uploadImage(
  original: File,
  noteId: string,
  onProgress?: (fraction: number) => void,
): Promise<UploadResult> {
  const uid = auth.currentUser?.uid
  if (!uid) throw new UploadError('Not signed in.')
  if (!original.type.startsWith('image/')) {
    throw new UploadError(`${original.name || 'That file'} is not an image.`)
  }

  // Shrink first: the limit then applies to what is actually stored, so a large
  // phone photo is accepted rather than rejected for being a large phone photo.
  const shrunk = await compressImage(original)
  const { file } = shrunk
  if (file.size > MAX_IMAGE_BYTES) {
    throw new UploadError(`Images must be under ${MAX_IMAGE_BYTES / 1024 / 1024} MB.`)
  }

  const name = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extensionFor(file)}`
  const objectRef = ref(storage, `users/${uid}/images/${noteId}/${name}`)
  const task = uploadBytesResumable(objectRef, file, { contentType: file.type })

  return new Promise<UploadResult>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => onProgress?.(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
      (err) => {
        const code = (err as { code?: string }).code ?? ''
        // The most common real-world failure: Storage never turned on for the project.
        if (code.includes('unknown') || code.includes('retry-limit')) {
          reject(new UploadError(
            'Upload failed. Is Cloud Storage enabled for this Firebase project, and are the storage rules deployed?',
          ))
        } else if (code.includes('unauthorized')) {
          reject(new UploadError('Upload denied by storage rules.'))
        } else {
          reject(new UploadError(err.message))
        }
      },
      () => {
        void getDownloadURL(task.snapshot.ref)
          .then((url) => resolve({ url, originalBytes: shrunk.originalBytes,
                                   bytes: shrunk.bytes, compressed: shrunk.compressed }))
          .catch(reject)
      },
    )
  })
}

const isAttachable = (t: string) => t.startsWith('image/') || ACCEPTED_FILE_TYPES.includes(t)

/** Images and PDFs from a drop or a paste. */
export function imageFilesFrom(data: DataTransfer | null): File[] {
  if (!data) return []
  const files = [...(data.files ?? [])].filter((f) => isAttachable(f.type))
  if (files.length) return files
  // Pasted screenshots arrive as items, not files.
  return [...(data.items ?? [])]
    .filter((i) => i.kind === 'file' && isAttachable(i.type))
    .map((i) => i.getAsFile())
    .filter((f): f is File => f !== null)
}

export interface FileUploadResult { url: string; name: string; size: number; mime: string }

/** Uploads a non-image attachment as-is; there is nothing useful to compress. */
export async function uploadFile(
  file: File, noteId: string, onProgress?: (fraction: number) => void,
): Promise<FileUploadResult> {
  const uid = auth.currentUser?.uid
  if (!uid) throw new UploadError('Not signed in.')
  if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
    throw new UploadError(`${file.name || 'That file'} isn’t a supported attachment.`)
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new UploadError(`Attachments must be under ${MAX_FILE_BYTES / 1024 / 1024} MB.`)
  }
  const safe = file.name.replace(/[^\w.\- ]+/g, '_').slice(0, 80) || 'document.pdf'
  const objectRef = ref(storage, `users/${uid}/files/${noteId}/${Date.now()}-${safe}`)
  const task = uploadBytesResumable(objectRef, file, { contentType: file.type })
  return new Promise<FileUploadResult>((resolve, reject) => {
    task.on('state_changed',
      (snap) => onProgress?.(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
      (err) => reject(new UploadError(err.message)),
      () => {
        void getDownloadURL(task.snapshot.ref)
          .then((url) => resolve({ url, name: file.name, size: file.size, mime: file.type }))
          .catch(reject)
      })
  })
}
